// 의료기관에 관련된
// 의료기관 리스트
// 영상 검색
// 영상 발급 요청
// 영상 결제
// 영상 전송
import axios, { AxiosResponse } from 'axios';

import { HSCAN_BACKEND_API_URL } from '../../env';
import { Page } from '../page';

import { AddressInfo } from './address';
import { Case } from './case';
import { PageQueryParams } from './types';

/**
 * * STUDY - 검사당 얼마
 * * SIMPLE - 건당 얼마
 * * IMAGE - 이미지 개수 당 얼마
 * * VOLUME - 용량 구간 당 얼마
 * * VOLUME2 - 용량과 매체 별 얼마
 */
export type PriceModel =
  | { type: 'STUDY'; amount: number; taxFree?: number }
  | { type: 'SIMPLE'; amount: number; taxFree?: number }
  | {
      type: 'IMAGE';
      amount: number;
      per: number;
      taxFree?: number;
    }
  | VolumePriceModel
  | VolumePrice2Model;

export interface VolumePriceModel {
  type: 'VOLUME';
  amounts: {
    /**
     * Bytes
     */
    lower: number;
    /**
     * Bytes  \
     * undefined if no limit
     */
    upper?: number;
    amount: number;
    taxFree?: number;
  }[];
  useExpectedPrice: boolean;
}

export interface MediaUnit {
  name: string;
  virtualVolume: number;
  /**
   * in Bytes
   */
  volume: number;
  price: number;
  unit?: string;
  taxFree?: number;
}

export interface ModalityVirtualVolume {
  virtualSize: number;
}

export interface VolumePrice2Model {
  type: 'VOLUME2';
  units: MediaUnit[];
  modalities: Record<string, ModalityVirtualVolume>;
  defaultOption: ModalityVirtualVolume;
  useExpectedPrice: boolean;
}
export interface Hospital {
  id: string;
  name: string;
  description?: string;
  registrationNumberRequired: boolean;
  price: PriceModel;
  cdPrice?: PriceModel;
  /**
   * 병원의 주소, 소재지를 적고 싶음
   */
  address: string;
  /**
   * 병원에 영상 전달할 때 병원에서 환자에게 보여주는 메세지
   */
  comment?: string;
  useMailing: boolean;
  mailingMandatory: boolean;
  loadEnabled: boolean;
  storeEnabled: boolean;
  onlineIssuanceMessage?: string;
}

export interface RequestHospitalsParams {
  /**
   * 검색어
   */
  query?: string;
}
export const requestHospitals = ({ query }: RequestHospitalsParams) => {
  return axios.get<Hospital[]>(`${HSCAN_BACKEND_API_URL}/hospital`, {
    params: { query },
  });
};

export interface RequestHospitalParams {
  id: string;
}
export const requestHospital = (
  { id }: RequestHospitalParams,
  signal?: AbortSignal,
) => {
  return axios.get<Hospital>(`${HSCAN_BACKEND_API_URL}/hospital/${id}`, {
    signal,
  });
};

/**
 * 신청가능: AVAILABLE
 * 신청완료: PENDING
 * 발급완료: COMPLETED
 * 발급실패: FAILED
 * 발급거절: DECLINED
 * 발급실패: CANCELLED
 */
export type REQUEST_STATUS =
  | 'AVAILABLE'
  | 'PENDING'
  | 'DECLINED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

/**
 * 병원에서 갖고 있는 PACS 에 있는 영상. `Case` 와는 다르게 HScan 에 저장돼있지 않을 수 있다
 * ImagingStudy 에 modality aggregation, bodySite (study description) 추가된 것
 */
export interface Study {
  studyInstanceUID: string;
  /**
   * 촬영 날짜
   * yyyy-MM-dd
   */
  date: string;
  /**
   * study 에 있는 영상 종류
   */
  modalities: string[];
  /**
   * bodypart (bodypart 가 없어서 studyDescription 으로 넘겨줌)
   */
  studyDescription: string;
  /**
   * 해당 영상의 HScan 에서의 발급 신청 여부.
   */
  status?: REQUEST_STATUS;
  numImages: number;
}

export interface RequestStudiesFromHospitalParams {
  id: string;
  registration?: string;
}
/**
 * 병원에서 본인의 영상 검색
 */
export const requestStudiesFromHospital = ({
  id,
  registration,
}: RequestStudiesFromHospitalParams) => {
  return axios.get<Study[]>(`${HSCAN_BACKEND_API_URL}/hospital/${id}/study`, {
    headers: {
      Registration: registration ? registration.replaceAll('-', '') : undefined,
    },
  });
};

export interface RequestSizeOfStudyParams {
  id: string;
  registration?: string;
  studyInstanceUID: string;
}
/**
 * 영상의 용량 구하기
 */
export const requestFileSizeOfStudy = ({
  id,
  registration,
  studyInstanceUID,
}: RequestSizeOfStudyParams) => {
  return axios.get<number>(
    `${HSCAN_BACKEND_API_URL}/hospital/${id}/study/${studyInstanceUID}/file-size`,
    {
      headers: {
        Registration: registration
          ? registration.replaceAll('-', '')
          : undefined,
      },
    },
  );
};

// ----------------------------------------------------------------------------

/**
 * 사용자가 한 발급 요청
 */
export interface StudyIssuanceRequest {
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  createdAt: string;
  hospitalId: string;
  hospitalName?: string;
  id: string;
  modalities: string[];
  numImages: number;
  status: REQUEST_STATUS;
  /**
   * yyyy-MM-dd
   */
  studyDate: string;
  studyDescription: string;
  studyInstanceUID: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  updatedAt: string;
  userId: string;
  /**
   * 우편 발송 여부 (발급 완료된 경우만 갖고 있음)
   * * false: online 으로 발급됨
   * * true: 우편으로 발급됨
   * * undefined: (발급 완료를 제외한 모든 study)
   */
  mailingIncluded?: boolean;
}

export interface StudyIssuanceRequestHistory {
  id: string;
  requestId: string;
  status: REQUEST_STATUS;
  hospitalName?: string;
  comment?: string;
  createdAt: string;
}

export interface RequestRequestedImageHistoryParams {
  id: string;
}
export const requestRequestedImageHistory = ({
  id,
}: RequestRequestedImageHistoryParams) => {
  return axios.get<StudyIssuanceRequestHistory[]>(
    `${HSCAN_BACKEND_API_URL}/hospital/study/request/${id}/history`,
  );
};

// ----------------------------------------------------------------------------

export interface PriceDetail {
  amount: number;
  refundableAmount: number;
  taxFree?: number;
}
export interface PriceInfo {
  /**
   * perHospitalFee
   * @param hospitalId key
   * @param PriceDetail value
   * */
  perHospitalFee: Record<string, PriceDetail>;
  deliveryFee: PriceDetail;
  totalFee: number;
  refundableFee: number;
  taxFree?: number;
}

/**
 * PAID: 결제완료
 * PARTIAL_REFUND: 일부환불
 * ALL_REFUND: 전액환불
 */
export type PAYMENT_STATUS = 'READY' | 'PAID' | 'PARTIAL_REFUND' | 'ALL_REFUND';
/**
 * ORDER_PENDING: 발송예정
 * ORDER_MAIL_AVAILABLE: 발송가능
 * ORDER_MAILED: 발송완료
 * ORDER_ONLINE_DONE: 온라인발급
 * ORDER_MAIL_CANCELLED: 발송취소
 */
export type MAILING_STATUS =
  | 'ORDER_PENDING'
  | 'ORDER_MAIL_AVAILABLE'
  | 'ORDER_MAILED'
  | 'ORDER_ONLINE_DONE'
  | 'ORDER_MAIL_CANCELLED';
export interface IssuePayment {
  id: string;
  type: 'ISSUE';
  userId: string;

  price: PriceInfo;
  paymentStatus: PAYMENT_STATUS;
  receiptUrl: string;
  paymentMethod?: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  paidAt?: string;

  requestIds: string[];
  hospitalName: string[];
  modalities?: string[];

  mailingAddress?: AddressInfo;
  mailStatus: MAILING_STATUS;
  trackingNumber?: string;
  shippingCompany?: string;
  sender?: string;
  /**
   * yyyy-MM-dd
   */
  sentAt?: string;
}

export interface SimplePayment {
  id: string;
  type: 'SIMPLE';
  userId: string;

  price: PriceDetail;
  paymentStatus: PAYMENT_STATUS;
  receiptUrl: string;
  paymentMethod: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  paidAt: string;
}

export interface CdDeliveryPayment {
  id: string;
  type: 'CD_DELIVERY';
  userId: string;

  price: PriceDetail;
  paymentStatus: PAYMENT_STATUS;
  receiptUrl: string;
  paymentMethod: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  paidAt: string;

  caseIds: string[];

  mailingAddress?: AddressInfo;
  mailStatus: MAILING_STATUS;
  trackingNumber?: string;
  shippingCompany?: string;
  sender?: string;
  /**
   * yyyy-MM-dd
   */
  sentAt?: string;
}

export interface OnlinePayment {
  id: string;
  type: 'ADMIN_STUDIES_ONLINE';
  userId: string;

  price: PriceDetail;
  paymentStatus: PAYMENT_STATUS;
  receiptUrl: string;
  paymentMethod: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  paidAt: string;

  hospitalName: string;
  content: {
    studyInstanceUID: string;
    modality: string;
    /**
     * yyyy-MM-dd
     */
    studyDate: string;
    studyDescription: string;
    /**
     * Bytes
     */
    fileSize: number;
    numberOfFiles: number;
  }[];
}

export type Payment =
  | IssuePayment
  | SimplePayment
  | CdDeliveryPayment
  | OnlinePayment;

/**
 * requestPay 의 응답으로만 사용하고 있음.
 * 결제 전에 만들어지는 것이라 receiptUrl 등이 없음
 */
export interface DicomImagePayment {
  id: string;
  userId: string;

  price: PriceInfo;
  requestIds: string[];
  paymentStatus: PAYMENT_STATUS;
  receiptUrl?: string;
  paymentMethod?: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  paidAt?: string;

  hospitalName: string[];
  modalities?: string[];

  mailingIncluded: boolean;
  mailingAddress?: AddressInfo;
  mailStatus: MAILING_STATUS;
}

export interface VerifyPaymentParams {
  id: string;
}
/**
 * 결제 모듈을 이용해 결제에 성공한 경우 서버에서 실제 결제를 확인 후 처리를 위한 요청
 */
export const verifyPayment = ({ id }: VerifyPaymentParams) => {
  return axios.put(
    `${HSCAN_BACKEND_API_URL}/hospital/study/payment/${id}`,
    undefined,
  );
};

export type RequestPaymentsParams = PageQueryParams;
export const requestPayments = (params: RequestPaymentsParams) => {
  return axios.get<Page<Payment>>(
    `${HSCAN_BACKEND_API_URL}/hospital/study/payment`,
    {
      params,
    },
  );
};

export interface RequestIssuancesInPaymentParam {
  paymentId: string;
}
export const requestIssuancesInPayment = (
  params: RequestIssuancesInPaymentParam,
) => {
  return axios.get<StudyIssuanceRequest[]>(
    `${HSCAN_BACKEND_API_URL}/hospital/study/payment/${params.paymentId}/request`,
    {
      params,
    },
  );
};

interface AnonymousUser {
  /**
   * +821012341234
   */
  phone: string;
  gender?: string;
  birth: Date;
}
export interface AdminStudiesConsent {
  id: string;
  bundleId: string;
  studiesInBundle: string[];
  handledStudies: string[];
  caseList: Case[];
  gatewayId: string;
  storeDelegateId?: string;
  destGatewayId?: string;
  destGatewayName?: string;
  createdAt: Date;
  user: AnonymousUser;
  userId: string;
  hospitalName: string;
  uuid: string;
}
export const requestLocalIssuanceInPayment = ({
  paymentId,
}: RequestIssuancesInPaymentParam) => {
  return axios.get<AdminStudiesConsent>(
    `${HSCAN_BACKEND_API_URL}/hospital/study/payment/${paymentId}/admin-studies-consent`,
  );
};

/**
 * 우편 발송 주문에 대한 정보.
 * 발급 요청할 때 우편 발송을 체크한 경우 생긴다.
 * 관리자 페이지에서 쓰이는 정보가 담겨있고 사용자에게 중요한 건 trackingNumber 이다.
 * 현재 이 타입에서 trackingNumber 빼고는 다 지워도 무방하다.
 */
export interface MailingOrder {
  paymentId: string;
  trackingNumber?: string;
  shippingCompany: string;
  sender: string;
  /**
   * yyyy-MM-dd
   */
  sentAt: string;
}

interface RequestPaymentMailingOrderParams {
  paymentId: string;
}
export const requestPaymentMailingOrder = ({
  paymentId,
}: RequestPaymentMailingOrderParams) => {
  return axios.get<MailingOrder>(
    `${HSCAN_BACKEND_API_URL}/hospital/study/payment/${paymentId}/order`,
  );
};

// ----------------------------------------------------------------------------

export type StoreRequestStatus =
  | 'PENDING'
  | 'DECLINED'
  | 'COMPLETED'
  | 'FAILED';

export interface StoreRequestBundle {
  id: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  createdAt: string;
  userId: string;
  hospitalName: string;
  /**
   * StoreRequest 의 id
   */
  requestIds: string[];
  /**
   * requestIds 에 있는 requests 의 status의 종합
   * status가 모두 같은 경우 -> 하나의 status가 넘어옴 ex) ['COMPLETED']
   * status가 일부 다른 경우 -> 여러 개의 status가 넘어옴 ex) ['PENDING', 'COMPLETED']
   */
  status: StoreRequestStatus[];
}

export type RequestStoreBundleParams = PageQueryParams;
export const requestStoreBundle = (params: RequestStoreBundleParams) => {
  return axios.get<Page<StoreRequestBundle>>(
    `${HSCAN_BACKEND_API_URL}/hospital/study/store-request-bundle`,
    {
      params,
    },
  );
};

export interface RequestStoreFormsInBundleParams {
  bundleId: string;
}
export const requestStoreFormsInBundle = (
  params: RequestStoreFormsInBundleParams,
) => {
  return axios.get<StoreRequest[]>(
    `${HSCAN_BACKEND_API_URL}/hospital/study/store-request-bundle/${params.bundleId}/request`,
    {
      params,
    },
  );
};

export interface StoreRequest {
  id: string;
  bundleId: string;
  userId: string;
  hospitalId: string;
  hospitalName: string;
  institutionName?: string;
  studyInstanceUID: string;
  status: StoreRequestStatus;
  /**
   * yyyy-MM-dd
   */
  studyDate?: string;
  modalities?: string[];
  studyDescription?: string;
  files: string[]; // 파일 이름들
  createdAt: Date;
  updatedAt: Date;
}

export interface PushStudyParams {
  id: string;
  registration?: string;
}
export interface PushStudyBody {
  caseId: string[];
}
export const pushStudy = (
  { id, registration }: PushStudyParams,
  body: PushStudyBody,
) => {
  return axios.post<never, AxiosResponse<never>, PushStudyBody>(
    `${HSCAN_BACKEND_API_URL}/hospital/${id}/study`,
    body,
    {
      headers: {
        Registration: registration
          ? registration.replaceAll('-', '')
          : undefined,
      },
    },
  );
};

export interface StoredRequestHistory {
  id: string;
  requestId: string;
  status: REQUEST_STATUS;
  hospitalName?: string;
  comment?: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  createdAt: string;
}

interface RequestStoredImageHistoryParams {
  id: string;
}
export const requestStoredImageHistory = ({
  id,
}: RequestStoredImageHistoryParams) => {
  return axios.get<StoredRequestHistory[]>(
    `${HSCAN_BACKEND_API_URL}/hospital/study/store-request/${id}/history`,
  );
};

// ----------------------------------------------------------------------------

/**
 * @param key hospitalId
 * @param type studies
 */
export type HospitalStudies = Record<string, Study[]>;
export interface PaymentBody {
  mailingAddress?: AddressInfo;
  mailingIncluded: boolean;
  price: number;
  requestStudies: HospitalStudies;
  /**
   * 예약 전송 (optional)
   */
  destGatewayId?: string;
}
export const requestPay = (body: PaymentBody, registration?: string) => {
  return axios.post<
    DicomImagePayment,
    AxiosResponse<DicomImagePayment>,
    PaymentBody
  >(`${HSCAN_BACKEND_API_URL}/hospital/study/payment`, body, {
    headers: {
      registration,
    },
  });
};
